import { Link, useParams } from "react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useAsync } from "../lib/useAsync";
import { IconCheck, IconReceipt, IconShield, IconX } from "../ui/icons";
import { ErrorState, Grade, Loading, PageHeader, SpecList, type PublicSpec } from "../ui/kit";

type ReceiptData = {
  code: string;
  verifyUrl: string;
  item: { title: string; specs: PublicSpec[]; condition: string; accessories: string[] };
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  currency: string;
  seller: { name: string; city: string | null };
  buyer: { name: string };
  meetup: { place: string; at: string | null } | null;
  checklist: { key: string; done: boolean }[];
  sellerConfirmedAt: string;
  buyerConfirmedAt: string;
  completedAt: string;
};

/**
 * The bill of sale: what changed hands, between whom, for how much, and the buyer's checks. Proof of a lawful
 * purchase for the buyer (useful if a phone is ever questioned) and a clean record for the seller. Printable.
 */
export function Receipt() {
  const { id } = useParams();
  const { t, money, date } = useI18n();
  const { data: r, error, loading, reload } = useAsync(() => api<ReceiptData>(`/api/conversations/${id}/receipt`), [id]);
  if (loading && !r) return <Loading />;
  if (error || !r) return <ErrorState error={error} onRetry={reload} />;
  const when = (iso: string) => date(iso, { day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <div className="receipt-page" style={{ maxWidth: 720, margin: "0 auto" }}>
      <PageHeader title={t("receipt.title")} back={`/inbox/${id}`} actions={<button className="btn sm no-print" onClick={() => window.print()}>{t("receipt.print")}</button>} />
      <article className="receipt card">
        <header className="row between">
          <span className="wordmark">Walashy</span>
          <span className="receipt-code"><IconReceipt size={18} /> <span className="num" dir="ltr">{r.code}</span></span>
        </header>
        <h2 dir="auto">{r.item.title}</h2>
        <p className="row" style={{ gap: 8 }}><Grade c={r.item.condition} /> {t(`cond.${r.item.condition}` as MessageKey)}</p>
        <dl className="detail-pairs">
          <div><dt>{t("receipt.seller")}</dt><dd dir="auto">{r.seller.name}{r.seller.city ? ` · ${r.seller.city}` : ""}</dd></div>
          <div><dt>{t("receipt.buyer")}</dt><dd dir="auto">{r.buyer.name}</dd></div>
          <div><dt>{t("receipt.price")}</dt><dd className="num">{r.quantity > 1 ? `${r.quantity} × ${money(r.unitPriceCents, r.currency)} = ` : ""}<b>{money(r.totalCents, r.currency)}</b></dd></div>
          <div><dt>{t("receipt.payment")}</dt><dd>{t("receipt.paidDirectly")}</dd></div>
          {r.meetup && <div><dt>{t("receipt.where")}</dt><dd dir="auto">{r.meetup.place}</dd></div>}
          {r.item.accessories.length > 0 && <div><dt>{t("listing.accessories")}</dt><dd dir="auto">{r.item.accessories.join("، ")}</dd></div>}
          <div><dt>{t("receipt.sellerConfirmed")}</dt><dd>{when(r.sellerConfirmedAt)}</dd></div>
          <div><dt>{t("receipt.buyerConfirmed")}</dt><dd>{when(r.buyerConfirmedAt)}</dd></div>
        </dl>
        <SpecList specs={r.item.specs} />
        <section>
          <h3>{t("deal.checklist")}</h3>
          <ul className="checklist read">
            {r.checklist.map((c) => (
              <li key={c.key} className={c.done ? "done" : ""}>
                {c.done ? <IconCheck size={16} /> : <IconX size={16} />} {t(`check.${c.key}` as MessageKey)}
              </li>
            ))}
          </ul>
        </section>
        <footer className="small muted">
          <IconShield size={16} /> {t("receipt.verify")} <span className="num" dir="ltr">{r.verifyUrl}</span>
          <p>{t("receipt.disclaimer")}</p>
        </footer>
      </article>
    </div>
  );
}

/** Public check of a bill of sale by its code: confirms the item and date, never the price or full names. */
export function VerifyReceipt() {
  const { code } = useParams();
  const { t, date } = useI18n();
  const { data, error, loading, reload } = useAsync(
    () => api<{ valid: boolean; title?: string; condition?: string; quantity?: number; completedAt?: string; seller?: string; buyer?: string }>(`/api/receipts/${code}`),
    [code],
  );
  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorState error={error} onRetry={reload} />;
  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <PageHeader title={t("receiptCheck.title")} />
      {data.valid ? (
        <div className="card stack verify ok">
          <strong className="row"><IconCheck /> {t("receiptCheck.valid")}</strong>
          <span dir="auto">{data.quantity! > 1 ? `${data.quantity} × ` : ""}{data.title}</span>
          <span className="small">{t(`cond.${data.condition}` as MessageKey)} · {date(data.completedAt!)}</span>
          <span className="small muted">{t("receiptCheck.between", { s: data.seller!, b: data.buyer! })}</span>
        </div>
      ) : (
        <div className="card stack verify bad">
          <strong className="row"><IconX /> {t("receiptCheck.invalid")}</strong>
          <span className="small muted">{t("receiptCheck.invalidBody")}</span>
        </div>
      )}
      <p className="small" style={{ marginTop: "var(--s5)" }}><Link to="/">{t("welcome.browse")}</Link></p>
    </div>
  );
}
